import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import type { Paged, Pagination, TimeFrame } from "@/types";
import { StoreModule } from "@/constants/store.ts";
import { type DevicesFilters, type TicketsFilters, useFiltersStore } from "@/_store/filters.module";
import { useSnackbarStore } from "@/_store/snackbar.module";
import moment from "moment";
import { i18n } from "@/plugins/i18n";
import type { ActivityLogsFilters } from "@/_store/activity-logs.module";
import type { ISelection } from "@/_store/selector.module";
import { convertDevicesGroupsFilter } from "@/_store/devices.module";
import { TicketStatus } from "@/constants/tickets";
import type { Service } from "@/constants/cloud-apps";
import type { UserExceptionsFilters } from "@/_store/users-settings/user-exceptions-tab.module";
import { convertTimeFrameToUnix } from "@/_helpers/utils.ts";
import { getCurrentDate, getNinetyDaysAgo } from "@/constants/date-range-picker.ts";
import { ExportChoice, type ExportListItem } from "@/constants/exports.ts";

export interface ExportDialogPayload<K> {
  type: ExportChoice;
  filters: K;
}

interface ExportsStoreState {
  loading: boolean;
  exportsList: Paged<ExportListItem>;
  pagination: Pagination;
}

const defaultReportsState: ExportsStoreState = {
  loading: false,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  exportsList: {
    items: [],
    total: 0,
  },
};

export const useExportsStore = defineStore(StoreModule.EXPORTS, {
  state: (): ExportsStoreState => ({ ...defaultReportsState }),
  actions: {
    async getExportsList() {
      const filters = convertFiltersForBackend(useFiltersStore().filters.exportsFilters);
      const request = {
        ...api.getExportsList(),
        params: {
          ...filters,
          ...this.pagination,
        },
      };
      try {
        this.loading = true;
        const { data } = await axiosInstance.request(request);
        this.exportsList = data;
        this.loading = false;
      } catch (e) {
        console.error(e);
        this.loading = false;
      }
    },
    async exportActivityLogsToCsv(
      payload: ExportDialogPayload<ActivityLogsFilters>
    ): Promise<void> {
      const params =
        payload.type === ExportChoice.SPECIFIC ? convertFiltersForBackend(payload.filters) : {};
      try {
        const request = {
          ...api.activityLogsExportCSV,
          params,
        };

        await axiosInstance.request(request);

        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.exports.exportCsvStarted")
        );
      } catch (error) {
        console.error(error);
      }
    },
    async exportPhishingSimulationReportToCsv(payload: TimeFrame) {
      const convertedTimeFrame = convertTimeFrameToUnix(payload);
      const request = {
        ...api.exportPhishingSimulationToCsv(),
        params: {
          ...convertedTimeFrame,
        },
      };
      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.exports.exportCsvStarted")
        );
      } catch (err) {
        console.error(err);
      }
    },
    async exportProtectedUsersToCsv(payload: ExportDialogPayload<UserExceptionsFilters>) {
      const request = {
        ...api.exportUsersCsv,
        params: {
          cloudServices:
            payload.type === ExportChoice.SPECIFIC
              ? payload.filters.cloudServices
                  .filter((v) => v.type === "cloudService")
                  .map((v) => v.name as Service)
                  .join(",")
              : undefined,
        },
      };
      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.exports.exportCsvStarted")
        );
      } catch (e) {
        console.error(e);
      }
    },
    async exportDevicesToCsv(payload: ExportDialogPayload<DevicesFilters>) {
      const filter =
        payload.type === ExportChoice.ALL
          ? {}
          : {
              ...payload.filters,
              ...convertDevicesGroupsFilter(payload.filters.labels),
            };
      const requestData: {
        selection: ISelection;
        filter: { [key: string]: unknown };
      } = {
        selection: {
          isAllPages: true,
          include: [],
          exclude: [],
        },
        filter,
      };
      const request = {
        ...api.devicesCsvExport,
        data: requestData,
      };
      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.exports.exportCsvStarted")
        );
      } catch (e) {
        console.error(e);
      }
    },
    async exportTicketsToCsv(payload: ExportDialogPayload<TicketsFilters>) {
      const filters =
        payload.type === ExportChoice.ALL
          ? {
              protectedUsersOnly: false,
              fromTime: moment(getNinetyDaysAgo()).startOf("day").utc().valueOf(),
              toTime: moment(getCurrentDate()).endOf("day").utc().valueOf(),
            }
          : {
              ...convertTicketFiltersForBackend({ ...payload.filters }),
              protectedUsersOnly: false,
              processed: null,
            };
      const request = {
        ...api.exportTicketsToCsv(),
        data: {
          filters,
          selection: {
            isAllPages: true,
            include: [],
            exclude: [],
          },
        },
      };
      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.exports.exportCsvStarted")
        );
      } catch (err) {
        console.error(err);
      }
    },
    async exportSecurityTrainingReportToCsv(payload: TimeFrame) {
      const convertedTimeFrame = convertTimeFrameToUnix(payload);
      const request = {
        ...api.exportSecurityTrainingToCsv(),
        params: {
          ...convertedTimeFrame,
        },
      };
      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.exports.exportCsvStarted")
        );
      } catch (err) {
        console.error(err);
      }
    },
  },
});

function convertFiltersForBackend<
  T extends {
    timeFrame: { end: string; start: string };
    search?: string | null;
    types?: (string | number)[];
  },
>({ timeFrame: { end, start }, search, types }: T) {
  return {
    search,
    types: types?.join(","),
    toTime: end ? moment(end).endOf("day").utc().valueOf() : null,
    fromTime: start ? moment(start).startOf("day").utc().valueOf() : null,
  };
}

export const convertTicketFiltersForBackend = (filters: TicketsFilters) => {
  const processed =
    filters.status === TicketStatus.CLOSED
      ? true
      : filters.status === TicketStatus.OPEN
        ? false
        : undefined;
  const fromTime = filters.eventTimeRange?.start
    ? moment(filters.eventTimeRange.start).startOf("day").utc().valueOf()
    : moment(getNinetyDaysAgo()).startOf("day").utc().valueOf();
  const toTime = filters.eventTimeRange?.end
    ? moment(filters.eventTimeRange.end).endOf("day").utc().valueOf()
    : moment(getCurrentDate()).endOf("day").utc().valueOf();
  return {
    ...filters,
    processed,
    fromTime,
    toTime,
  };
};
